/**
 * Lignes de l'aperçu structurel du Drawer, à partir de la forme déclarée par
 * la variante (cf. `previewShapes` dans tableDescriptors).
 *
 * Les lignes sont construites comme celles de l'API : un objet indexé par
 * libellé de colonne, libellé dans `labelColumn`, montant dans `totalsColumn`.
 * Masquer une colonne retire donc ce qu'elle porte, comme dans le document, et
 * la fusion auto des lignes-titre est celle du document (computeLabelRowMerges).
 *
 * Module partagé document ↔ site : pur, sans React ni store.
 */

import { computeLabelRowMerges } from './tableStructure';
import { getRowStyleState } from './tableRowStyle';

const AMOUNT = '—';

const cellValue = (row, column, { labelColumn, totalsColumn }) => {
  if (column === labelColumn) return row.label ?? '';
  if (row.kind === 'item' || row.kind === 'fixed') {
    return column === totalsColumn && row.unpriced ? '' : AMOUNT;
  }
  if (['zone', 'subtotal', 'total', 'line'].includes(row.kind)) {
    return column === totalsColumn && !row.unpriced ? AMOUNT : '';
  }
  return row.fill?.includes(column) ? AMOUNT : '';
};

// Libellé propre à chaque affaire, sans type : rien à y régler.
const UNSELECTABLE_KINDS = ['item', 'line'];

/**
 * @param {object[]} shape    lignes { kind, label?, unpriced? }
 * @param {string[]} columns  libellés d'origine des colonnes visibles, dans l'ordre
 * @param {{ labelColumn, totalsColumn, structure }} layout
 * @param {object} mergeOptions  réglages `tableConfig.merge`
 * @returns {{ key, kind, cells: object, merge: { from, span }|null, labelMerge: object|null }[]}
 *   merge : fusion de la ligne (indice de départ, nombre de colonnes, colonne du
 *   texte si elle diffère de la première), ou null.
 *   labelMerge : ligne que visent sa fusion et son style (cf. getRowStyleState),
 *   pour la régler d'un clic dans l'aperçu ; null si rien ne s'y règle.
 */
export const buildTablePreviewRows = (shape, columns, layout, mergeOptions) => {
  const rows = (shape || []).map((row, index) => {
    const cells = {};
    columns.forEach((column) => {
      cells[column] = cellValue(row, column, layout);
    });
    return { key: `preview_${index}`, kind: row.kind, cells };
  });
  // Une ligne typée porte son type, comme celles du document (`rowKind`) : sa
  // fusion se règle pour toutes les lignes de ce type, quel que soit leur nom.
  const rowKindOf = (row) => row.rowKind ?? (row.kind === 'zone' || row.kind === 'title' ? row.kind : null);
  const tableRows = rows.map((row, index) => ({
    key: row.key,
    ...(rowKindOf(shape[index]) ? { rowKind: rowKindOf(shape[index]) } : {}),
    ...row.cells,
  }));
  const tableColumns = columns.map((column) => ({ key: column }));
  const merges = computeLabelRowMerges(tableRows, tableColumns, undefined, mergeOptions);
  const mergeByKey = new Map(
    merges.map((merge) => [
      merge.rowKeys[0],
      {
        from: columns.indexOf(merge.columnKeys[0]),
        span: merge.columnKeys.length,
        // Colonne dont le texte remplit la cellule fusionnée (libellé ramené à gauche).
        contentColumn: merge.contentKey,
      },
    ])
  );
  return rows.map((row, index) => ({
    ...row,
    merge: mergeByKey.get(row.key) ?? null,
    labelMerge: UNSELECTABLE_KINDS.includes(row.kind)
      ? null
      : getRowStyleState(tableRows[index], tableColumns, undefined, {
        ...mergeOptions,
        structure: layout?.structure,
      }),
  }));
};
